/**
 * Everything the running journeys have queued, waiting for a person to send it.
 *
 * Same hand-off as the campaign console: the message opens in the user's own
 * client and the queue records what happened.
 */
import { useApp } from '../../store/AppContext.jsx';
import { digitsOf, whatsappLink, download } from '../../lib/format.js';
import { channelLabel, channelPill } from '../../constants/marketing.js';
import Sheet, { Field } from '../Sheet.jsx';
import { Avatar, EmptyState } from '../Primitives.jsx';

export default function JourneyQueue({ onClose }) {
  const { mkt, setQueueStatus, markQueueAllSent, logMarketingTouch, toast } = useApp();

  const pending = (mkt.queue || []).filter(q => q.status === 'pending');

  const finish = (item, status) => {
    setQueueStatus(item.id, status);
    if (status === 'sent') {
      logMarketingTouch(item.rid, item.channel, item.subject || item.jname, `Journey: ${item.jname}`);
    }
  };

  const send = item => {
    if (item.channel === 'whatsapp') {
      if (!digitsOf(item.phone)) { toast('No phone number'); return; }
      window.open(whatsappLink(item.phone, item.body), '_blank');
    } else if (item.channel === 'email') {
      window.open(
        `mailto:${encodeURIComponent(item.email || '')}`
        + `?subject=${encodeURIComponent(item.subject || '')}&body=${encodeURIComponent(item.body)}`,
        '_blank'
      );
    } else if (item.channel === 'sms') {
      window.open(`sms:${digitsOf(item.phone)}?body=${encodeURIComponent(item.body)}`, '_blank');
    } else {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        toast('Enable push for this device in Channel setup first');
        return;
      }
      try {
        // eslint-disable-next-line no-new
        new Notification(item.subject || item.jname, { body: String(item.body).slice(0, 180) });
      } catch { /* blocked */ }
    }
    finish(item, 'sent');
  };

  const copyAll = () => {
    const text = pending
      .map(z => `— ${z.name}\n${z.subject ? `Subject: ${z.subject}\n` : ''}${z.body}`)
      .join('\n\n');
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast('Copied'), () => toast('Copy failed'));
  };

  const exportCSV = () => {
    const q = v => `"${String(v || '').replace(/"/g, '""')}"`;
    const lines = [['Name', 'Email', 'Phone', 'Channel', 'Subject', 'Message'].map(q).join(',')]
      .concat(pending.map(z => [z.name, z.email, z.phone, z.channel, z.subject, z.body].map(q).join(',')));
    download(
      new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      'journey-queue.csv'
    );
    toast(`Exported ${pending.length}`);
  };

  return (
    <Sheet
      icon="send"
      tone="g-amber"
      title={`Journey queue · ${pending.length} waiting`}
      onClose={onClose}
    >
      {pending.length ? (
        <>
          <Field label="" wide>
            <div className="rowb">
              <button type="button" className="btn sm" onClick={copyAll}>Copy them all</button>
              <button type="button" className="btn sm" onClick={exportCSV}>Export for a provider</button>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Mark all ${pending.length} as sent?`)) markQueueAllSent();
                }}
              >
                Mark everything sent
              </button>
            </div>
          </Field>

          <Field label="" wide>
            <div className="sendlist">
              {pending.map(item => (
                <div className="ob" style={{ margin: 0 }} key={item.id}>
                  <div className="obh">
                    <Avatar name={item.name} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <b>{item.name}</b>
                      <div className="sub">{item.jname} · step {item.step + 1}</div>
                    </div>
                    <span className={`chan ${item.channel === 'whatsapp' ? 'whatsapp' : 'email'}`}>
                      {channelLabel(item.channel)}
                    </span>
                  </div>

                  {item.subject ? (
                    <div className="dim" style={{ fontSize: 12, marginBottom: 5 }}>{item.subject}</div>
                  ) : null}

                  <div style={{
                    fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                    color: 'var(--ink2)', marginBottom: 9
                  }}
                  >
                    {String(item.body).slice(0, 220)}{String(item.body).length > 220 ? '…' : ''}
                  </div>

                  <div className="obacts">
                    <button type="button" className="btn sm primary" onClick={() => send(item)}>
                      {item.channel === 'email' ? 'Open in mail'
                        : item.channel === 'whatsapp' ? 'WhatsApp'
                          : item.channel === 'sms' ? 'Open SMS' : 'Push'}
                    </button>
                    <button type="button" className="btn sm" onClick={() => finish(item, 'sent')}>Mark sent</button>
                    <button
                      type="button"
                      className="btn sm danger"
                      onClick={() => setQueueStatus(item.id, 'discarded')}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Field>
        </>
      ) : (
        <Field label="" wide>
          <EmptyState icon="check" tone="g-green" title="Nothing queued">
            Run a journey, or wait for the next step to come due.
          </EmptyState>
        </Field>
      )}
    </Sheet>
  );
}
